import React from 'react';
import { ArtistPortalArtistRef } from 'fym-shared/src/smartlinks/artistPortals';
import { TypedMemo } from '../../common/types';
import { ErrorAlert } from '../../common/views/ErrorAlert/ErrorAlert';
import { ErrorAPIResult } from '../../../redux/api/utils/isSuccessfulResult';
import { ConnectChoice, ConnectServiceChoices } from './ConnectServiceChoices';
import { ArtistPortalCredentialsForm } from './ArtistPortalCredentialsForm';
import { ArtistPortalArtistPicker } from './ArtistPortalArtistPicker';
import { PhoneConnectHandoff } from '../../mobileApps/PhoneConnectHandoff';
import { SpotifyAuthWebView } from './artistPortalAuth/SpotifyAuthWebView';
import { ConnectRequestSent } from './ConnectRequestSent';

export type ConnectStep = 'choices' | 'credentials' | 'picker' | 'requested';

interface ConnectServiceModalBodyProps {
  accountId: number;
  integrationType: string | undefined;
  onClose: () => void;
  step: ConnectStep;
  serviceName: string;
  canSignIn: boolean;
  isBusy: boolean;
  isUnsupported: boolean;
  choice: ConnectChoice | undefined;
  onChoose: (choice: ConnectChoice) => void;
  artists: ArtistPortalArtistRef[];
  onCredentials: (email: string, password: string) => void;
  onPickArtist: (externalArtistId: string) => void;
  error?: ErrorAPIResult['error'];
  portalError?: ErrorAPIResult['error'];
  /** Why a sign-in that returned through the server failed, ready to show. */
  callbackFailureMessage?: string;
}

/** One step of the connect sheet at a time, plus whatever went wrong. */
const ConnectServiceModalBodyComponent: React.FC<ConnectServiceModalBodyProps> = ({
  accountId,
  integrationType,
  onClose,
  step,
  serviceName,
  canSignIn,
  isBusy,
  isUnsupported,
  choice,
  onChoose,
  artists,
  onCredentials,
  onPickArtist,
  error,
  portalError,
  callbackFailureMessage,
}) => {
  // A browser cannot finish this sign-in: the phone does it (design music24-mobile-apps 1a, 1d).
  if (isUnsupported && integrationType !== undefined) {
    return (
      <PhoneConnectHandoff
        accountId={accountId}
        integrationType={integrationType}
        serviceName={serviceName}
        onClose={onClose}
      />
    );
  }

  return (
    <>
      {error !== undefined && <ErrorAlert error={error} />}
      {portalError !== undefined && <ErrorAlert error={portalError} />}
      {callbackFailureMessage !== undefined && <ErrorAlert customMessage={callbackFailureMessage} />}
      {step === 'choices' && (
        <ConnectServiceChoices serviceName={serviceName} choice={choice} onSelect={onChoose} canSignIn={canSignIn} />
      )}
      {step === 'credentials' && (
        <ArtistPortalCredentialsForm serviceName={serviceName} isLoading={isBusy} onSubmit={onCredentials} />
      )}
      {step === 'requested' && <ConnectRequestSent />}
      {step === 'picker' && <ArtistPortalArtistPicker artists={artists} isLoading={isBusy} onSelect={onPickArtist} />}
      <SpotifyAuthWebView />
    </>
  );
};

export const ConnectServiceModalBody = TypedMemo(ConnectServiceModalBodyComponent);
